/**
 * The seam between the commerce application and whichever object store is
 * actually holding a merchant's media.
 *
 * Deliberately narrow. Everything the platform needs to do to an object goes
 * through one of these calls, so adding a provider is one new implementation
 * and no changes anywhere else. Note what is *not* here: there is no
 * `getUsage()`. Provider-side usage APIs are slow, eventually consistent, and
 * missing on some providers — usage is computed from our own media registry
 * and reconciled against `list()` on a schedule instead.
 */

export interface SaveResult {
  url: string;
  sizeBytes: number;
  storageKey: string;
  checksum?: string;
}

export interface ObjectMetadata {
  sizeBytes: number;
  mimeType: string;
  etag?: string;
  lastModified?: Date;
}

export interface ListPage {
  keys: string[];
  nextCursor?: string;
}

export interface PresignedUpload {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresAt: Date;
}

/**
 * Result of probing a backend. `ok` is the summary; the individual flags are
 * what the setup wizard shows the merchant, because "it failed" is useless
 * feedback when five different things could be wrong.
 */
export interface ConnectionProbeResult {
  ok: boolean;
  canList: boolean;
  canWrite: boolean;
  canRead: boolean;
  canDelete: boolean;
  publicReadOk: boolean;
  /** One of the health states in the plan (§12), for storing on the config. */
  state: StorageHealthState;
  message?: string;
}

export type StorageHealthState =
  | 'connected'
  | 'degraded'
  | 'auth_failed'
  | 'bucket_not_found'
  | 'permission_denied'
  | 'provider_unavailable'
  | 'config_error'
  | 'disconnected';

export interface StorageAdapter {
  save(buffer: Buffer, key: string, mimeType: string): Promise<SaveResult>;
  /** Bytes back out — needed by derivatives, migration and backup. */
  read(key: string): Promise<Buffer>;
  delete(key: string): Promise<void>;
  deleteMany(keys: string[]): Promise<void>;
  exists(key: string): Promise<boolean>;
  getMetadata(key: string): Promise<ObjectMetadata>;
  list(prefix: string, cursor?: string): Promise<ListPage>;
  /** The storefront `<img src>`. Public assets only — never expires. */
  publicUrl(key: string): string;
  /** Private assets only (documents). Short TTL, never cached, never logged. */
  createDownloadUrl(key: string, ttlSeconds: number): Promise<string>;
  createUploadUrl(key: string, mimeType: string, maxBytes: number): Promise<PresignedUpload>;
  testConnection(): Promise<ConnectionProbeResult>;
}

/** Legacy alias — the product-image upload path still injects by this name. */
export type ImageStorageAdapter = Pick<StorageAdapter, 'save' | 'delete'>;

export const IMAGE_STORAGE_ADAPTER = Symbol('IMAGE_STORAGE_ADAPTER');
